// src/components/profile/BillingSettings.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Divider,
  Card,
  CardContent,
  Button,
  Alert,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  TextField,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  CreditCard as CreditCardIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Settings as SettingsIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Security as SecurityIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  Launch as LaunchIcon,
  Notifications as NotificationsIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';

const BillingSettings = () => {
  const { user, updateProfile } = useAuth();
  const [billingData, setBillingData] = useState({
    stripeCustomerId: '',
    subscription: null,
    paymentMethods: [],
    billingAddress: {
      name: '',
      email: '',
      line1: '',
      line2: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'MX'
    },
    preferences: {
      autoRenew: true,
      emailInvoices: true,
      currency: 'MXN'
    }
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);

  // Mock data for demonstration
  const mockTransactions = [
    {
      id: 'pi_1234567890',
      date: '2024-12-15',
      amount: 299.00,
      currency: 'MXN',
      status: 'succeeded',
      description: 'Suscripción Premium - Diciembre 2024',
      invoice_url: '#'
    },
    {
      id: 'pi_0987654321',
      date: '2024-11-15',
      amount: 299.00,
      currency: 'MXN',
      status: 'succeeded',
      description: 'Suscripción Premium - Noviembre 2024',
      invoice_url: '#'
    },
    {
      id: 'pi_1122334455',
      date: '2024-10-15',
      amount: 299.00,
      currency: 'MXN',
      status: 'succeeded',
      description: 'Suscripción Premium - Octubre 2024',
      invoice_url: '#'
    }
  ];

  const mockSubscription = {
    id: 'sub_1234567890',
    status: 'active',
    plan: {
      name: 'Plan Premium',
      amount: 299.00,
      currency: 'MXN',
      interval: 'month'
    },
    current_period_start: '2024-12-15',
    current_period_end: '2025-01-15',
    cancel_at_period_end: false
  };

  const mockPaymentMethods = [
    {
      id: 'pm_1234567890',
      type: 'card',
      card: {
        brand: 'visa',
        last4: '4242',
        exp_month: 12,
        exp_year: 2025
      },
      is_default: true
    },
    {
      id: 'pm_0987654321',
      type: 'card',
      card: {
        brand: 'mastercard',
        last4: '5555',
        exp_month: 8,
        exp_year: 2026
      },
      is_default: false
    }
  ];

  useEffect(() => {
    // Cargar datos de facturación del usuario
    if (user?.billing) {
      setBillingData(prev => ({
        ...prev,
        ...user.billing
      }));
    }
    setTransactions(mockTransactions);
  }, [user]);

  const handleBillingAddressUpdate = async () => {
    setLoading(true);
    setMessage(null);

    try {
      await updateProfile({ 
        billing: {
          ...billingData,
          updatedAt: new Date()
        }
      });
      
      setMessage({ 
        type: 'success', 
        text: 'Dirección de facturación actualizada correctamente' 
      });
      setAddressDialogOpen(false);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: 'Error al actualizar la dirección de facturación' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStripeConnect = () => {
    // Aquí irías a la configuración de Stripe
    window.open('https://dashboard.stripe.com', '_blank');
  };

  const handleAddPaymentMethod = () => {
    // Aquí abres el checkout de Stripe para agregar método de pago
    setMessage({ 
      type: 'info', 
      text: 'Función de agregar método de pago próximamente disponible' 
    });
  };

  const handleSubscriptionManagement = () => {
    // Aquí navegas al portal del cliente de Stripe
    setMessage({ 
      type: 'info', 
      text: 'Portal de suscripciones próximamente disponible' 
    });
  };

  const getCardIcon = (brand) => {
    const icons = {
      visa: '💳',
      mastercard: '💳',
      amex: '💳',
      discover: '💳'
    };
    return icons[brand] || '💳';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'succeeded': return 'Exitoso';
      case 'pending': return 'Pendiente';
      case 'failed': return 'Fallido';
      default: return status;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatAmount = (amount, currency) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom fontWeight="bold">
        Facturación
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Gestiona tu información de facturación, métodos de pago y suscripciones
      </Typography>

      <Divider sx={{ mb: 3 }} />

      {/* Mensaje de estado */}
      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Suscripción actual */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <MoneyIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Suscripción Actual
                  </Typography>
                </Box>
                {mockSubscription && (
                  <Chip 
                    label={mockSubscription.status === 'active' ? 'Activa' : 'Inactiva'} 
                    color={mockSubscription.status === 'active' ? 'success' : 'default'}
                    size="small"
                  />
                )}
              </Box>

              {mockSubscription ? (
                <Box>
                  <Typography variant="h5" color="primary" gutterBottom>
                    {mockSubscription.plan.name}
                  </Typography>
                  <Typography variant="h6" gutterBottom>
                    {formatAmount(mockSubscription.plan.amount, mockSubscription.plan.currency)}
                    <Typography component="span" variant="body2" color="text.secondary">
                      /{mockSubscription.plan.interval === 'month' ? 'mes' : 'año'}
                    </Typography>
                  </Typography>
                  
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Próximo pago"
                        secondary={formatDate(mockSubscription.current_period_end)}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Renovación automática"
                        secondary={mockSubscription.cancel_at_period_end ? 'Cancelada' : 'Activa'}
                      />
                    </ListItem>
                  </List>

                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      startIcon={<SettingsIcon />}
                      onClick={handleSubscriptionManagement}
                      fullWidth
                    >
                      Gestionar Suscripción
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box textAlign="center" py={4}>
                  <Typography variant="body1" color="text.secondary" gutterBottom>
                    No tienes una suscripción activa
                  </Typography>
                  <Button variant="contained" color="primary">
                    Ver Planes
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Métodos de pago */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <CreditCardIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Métodos de Pago
                  </Typography>
                </Box>
                <IconButton 
                  color="primary" 
                  onClick={handleAddPaymentMethod}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              </Box>

              {mockPaymentMethods.length > 0 ? (
                <List>
                  {mockPaymentMethods.map((method) => (
                    <ListItem key={method.id}>
                      <ListItemIcon>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                          {getCardIcon(method.card.brand)}
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center">
                            <Typography variant="body2">
                              •••• •••• •••• {method.card.last4}
                            </Typography>
                            {method.is_default && (
                              <Chip 
                                label="Principal" 
                                size="small" 
                                color="primary" 
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        }
                        secondary={`${method.card.brand.toUpperCase()} • Exp ${method.card.exp_month}/${method.card.exp_year}`}
                      />
                      <ListItemSecondaryAction>
                        <IconButton size="small">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Box textAlign="center" py={2}>
                  <Typography variant="body2" color="text.secondary">
                    No hay métodos de pago configurados
                  </Typography>
                </Box>
              )}

              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddPaymentMethod}
                fullWidth
                sx={{ mt: 2 }}
              >
                Agregar Método de Pago
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Dirección de facturación */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center">
                  <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    Dirección de Facturación
                  </Typography>
                </Box>
                <IconButton 
                  color="primary" 
                  onClick={() => setAddressDialogOpen(true)}
                  size="small"
                >
                  <EditIcon />
                </IconButton>
              </Box>

              {billingData.billingAddress.name ? (
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    {billingData.billingAddress.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {billingData.billingAddress.email}
                  </Typography>
                  <Typography variant="body2">
                    {billingData.billingAddress.line1}
                  </Typography>
                  {billingData.billingAddress.line2 && (
                    <Typography variant="body2">
                      {billingData.billingAddress.line2}
                    </Typography>
                  )}
                  <Typography variant="body2">
                    {billingData.billingAddress.city}, {billingData.billingAddress.state} {billingData.billingAddress.postal_code}
                  </Typography>
                  <Typography variant="body2">
                    {billingData.billingAddress.country}
                  </Typography>
                </Box>
              ) : (
                <Box textAlign="center" py={2}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No hay dirección de facturación configurada
                  </Typography>
                  <Button 
                    variant="outlined" 
                    onClick={() => setAddressDialogOpen(true)}
                  >
                    Agregar Dirección
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Configuración de Stripe */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <SecurityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Configuración de Stripe
                </Typography>
              </Box>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Cuenta verificada"
                    secondary="Tu cuenta está configurada correctamente"
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemIcon>
                    <SecurityIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Pagos seguros"
                    secondary="Procesados por Stripe"
                  />
                </ListItem>
              </List>

              <Button
                variant="outlined"
                startIcon={<LaunchIcon />}
                onClick={handleStripeConnect}
                fullWidth
              >
                Abrir Dashboard de Stripe
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Historial de transacciones */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <ReceiptIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  Historial de Transacciones
                </Typography>
              </Box>

              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Descripción</TableCell>
                      <TableCell align="right">Monto</TableCell>
                      <TableCell align="center">Estado</TableCell>
                      <TableCell align="center">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {formatDate(transaction.date)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.description}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {transaction.id}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatAmount(transaction.amount, transaction.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={getStatusText(transaction.status)}
                            color={getStatusColor(transaction.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Descargar factura">
                            <IconButton size="small">
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {transactions.length === 0 && (
                <Box textAlign="center" py={4}>
                  <Typography variant="body2" color="text.secondary">
                    No hay transacciones registradas
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Preferencias de facturación */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preferencias de Facturación
              </Typography>

              <List>
                <ListItem>
                  <ListItemIcon>
                    <NotificationsIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Recibir facturas por email"
                    secondary="Te enviaremos las facturas a tu correo electrónico"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={billingData.preferences.emailInvoices}
                      onChange={(e) => setBillingData(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          emailInvoices: e.target.checked
                        }
                      }))}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>

                <ListItem>
                  <ListItemIcon>
                    <PaymentIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Renovación automática"
                    secondary="Renovar automáticamente la suscripción"
                  />
                  <ListItemSecondaryAction>
                    <Switch
                      checked={billingData.preferences.autoRenew}
                      onChange={(e) => setBillingData(prev => ({
                        ...prev,
                        preferences: {
                          ...prev.preferences,
                          autoRenew: e.target.checked
                        }
                      }))}
                      color="primary"
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Dialog para editar dirección de facturación */}
      <Dialog open={addressDialogOpen} onClose={() => setAddressDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Dirección de Facturación
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nombre completo"
                value={billingData.billingAddress.name}
                onChange={(e) => setBillingData(prev => ({
                  ...prev,
                  billingAddress: {
                    ...prev.billingAddress,
                    name: e.target.value
                  }
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={billingData.billingAddress.email}
                onChange={(e) => setBillingData(prev => ({
                  ...prev,
                  billingAddress: {
                    ...prev.billingAddress,
                    email: e.target.value
                  }
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección línea 1"
                value={billingData.billingAddress.line1}
                onChange={(e) => setBillingData(prev => ({
                  ...prev,
                  billingAddress: {
                    ...prev.billingAddress,
                    line1: e.target.value
                  }
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección línea 2 (opcional)"
                value={billingData.billingAddress.line2}
                onChange={(e) => setBillingData(prev => ({
                  ...prev,
                  billingAddress: {
                    ...prev.billingAddress,
                    line2: e.target.value
                  }
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Ciudad"
                value={billingData.billingAddress.city}
                onChange={(e) => setBillingData(prev => ({
                  ...prev,
                  billingAddress: {
                    ...prev.billingAddress,
                    city: e.target.value
                  }
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Estado"
                value={billingData.billingAddress.state}
                onChange={(e) => setBillingData(prev => ({
                  ...prev,
                  billingAddress: {
                    ...prev.billingAddress,
                    state: e.target.value
                  }
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Código postal"
                value={billingData.billingAddress.postal_code}
                onChange={(e) => setBillingData(prev => ({
                  ...prev,
                  billingAddress: {
                    ...prev.billingAddress,
                    postal_code: e.target.value
                  }
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddressDialogOpen(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleBillingAddressUpdate}
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BillingSettings;